/**
 * ═══════════════════════════════════════════════════════════════════════════
 * OUVRIR L'APPLICATION SANS RÉSEAU — l'enregistrement du service worker (D.940)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️⚠️ LE SERVICE WORKER EST LA SEULE TECHNIQUE DE CE DÉPÔT QUI PEUT SURVIVRE
 * À SON PROPRE RETRAIT. Une fois installé, il intercepte les requêtes d'un
 * navigateur qui ne charge plus notre code : le désinstaller exige de le
 * désinstaller, pas de supprimer un fichier. C'est pourquoi tout ici est
 * explicite, et pourquoi `desinscrire()` existe.
 *
 * ⚠️ PAS EN DÉVELOPPEMENT. Un worker actif sur le port 5173 servirait une
 * version figée pendant qu'on modifie le code — et c'est l'endroit où l'on
 * conclut que sa propre modification est fautive.
 *
 * ⚠️ PAS SUR LA SURFACE PUBLIQUE. `verifier.html` (§17) vérifie des documents
 * d'État. Servir une page de vérification depuis un cache dirait « vérifié »
 * sur un document qui ne l'est plus.
 */

export type EtatHorsLigne = 'inactif' | 'pret' | 'mise-a-jour-en-attente';

let workerEnAttente: ServiceWorker | null = null;

/**
 * Installe le service worker et prévient quand une version l'attend.
 *
 * ⚠️ IL NE BASCULE PAS TOUT SEUL. Un `skipWaiting()` automatique remplacerait
 * l'application au milieu d'une saisie de terrain — la file vit dans la page.
 * On prévient, l'agent décide.
 */
export function installerHorsLigne(quand: (etat: EtatHorsLigne) => void): void {
  if (!('serviceWorker' in navigator)) { quand('inactif'); return; }
  /* ⚠️ LE PORT, PAS `import.meta.env`. Ce fichier est typé par un tsconfig
     qui ne charge pas les types de Vite : `import.meta.env` n'y existe pas
     (TS2339). Le port du serveur de développement est observable, et il est
     déclaré au même endroit que le relais — `vite.config.ts`. */
  if (location.port === '5173') { quand('inactif'); return; }
  // La page publique ne s'enregistre pas — voir l'en-tête.
  if (location.pathname.includes('verifier')) { quand('inactif'); return; }

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').then((inscription) => {
      if (inscription.waiting) { workerEnAttente = inscription.waiting; quand('mise-a-jour-en-attente'); }
      else quand('pret');

      inscription.addEventListener('updatefound', () => {
        const neuf = inscription.installing;
        if (!neuf) return;
        neuf.addEventListener('statechange', () => {
          /* ⚠️ `controller` DISTINGUE LA PREMIÈRE INSTALLATION D'UNE MISE À
             JOUR. Sans lui, la toute première visite annoncerait « une nouvelle
             version vous attend » alors qu'elle vient d'installer la seule qui
             existe — et l'agent recharge pour rien, ce qui use la confiance
             qu'on lui demandera d'avoir le jour où ce sera vrai. */
          if (neuf.state === 'installed' && navigator.serviceWorker.controller) {
            workerEnAttente = neuf;
            quand('mise-a-jour-en-attente');
          }
        });
      });
    }).catch(() => quand('inactif'));
  });
}

/** Bascule sur la version qui attend, à la demande de l'agent. */
export function basculer(): void {
  if (!workerEnAttente) { location.reload(); return; }
  workerEnAttente.postMessage('basculer');
  // Le nouveau worker prend la main, puis la page se recharge une seule fois.
  let recharge = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (recharge) return;
    recharge = true;
    location.reload();
  });
}

/**
 * Retire le service worker et vide ses caches.
 *
 * ⚠️ IL EXISTE POUR QUE LE RETRAIT SOIT POSSIBLE. Un jour, quelqu'un devra
 * défaire ceci — parce que la technique gêne, parce qu'un défaut se révèle,
 * parce que le besoin disparaît. Sans cette fonction, il faudrait déployer un
 * worker dont le seul rôle serait de se désinstaller, et il faudrait y penser
 * AVANT d'en avoir besoin.
 */
export async function desinscrire(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const inscriptions = await navigator.serviceWorker.getRegistrations();
  await Promise.all(inscriptions.map((i) => i.unregister()));
  const noms = await caches.keys();
  await Promise.all(noms.filter((n) => n.startsWith('cadastral-')).map((n) => caches.delete(n)));
}
